import React, {forwardRef, useEffect, useImperativeHandle, useMemo} from 'react'
import {useDispatch} from 'react-redux'
import {useAuth0} from '@auth0/auth0-react'
import {useNavigate} from 'react-router'
import {useAccountTree} from './hooks/useAccountTree'
import {toLocationPayloadBillTo} from './utils/billTo'
import {AccountCard} from './components/AccountCard'
import {AddressSuggestionModal} from './components/AddressSuggestionModal'
import {goNext} from '../../../app-store/AccountSetupSlice'
import {
  setAccountInfo,
  initLocations,
  setProductConfigInfo,
} from '../../../app-store/PurchaseLinkSlice'
import {applyProductRulesBeforeLoading} from '../../../features/CatalogGrid/utils/productPreloadRules'
import {fetchKeapPartnerCatalog} from '../../../utils/fetch-keap-partner-catalog'
import {buildNewLocationOrderState} from '../AccountsTabs/newLocationOrderState'
import {buildBehalfRepFieldsForChildOrder} from './utils/behalfRep'
import {
  AccordionChildWrap,
  AccordionParentWrap,
  AccordionWrapper,
  ChildBadge,
  CountrySelectorColumn,
  DraftActions,
  DraftBanner,
  LoadingOverlay,
  // MultiLocationBadge,
  // OrderButton,
  PageHeader,
  PageSubtitle,
  PageTitle,
  PageWrapper,
  ParentBadge,
  // SaveButton,
  SpinnerLabel,
  SpinnerRing,
  TopControlsRow,
  TransactionTypeColumn,
} from '../AccountInfo/AccountInfo.style'
import {Accordion} from './components/Accordion'
import {CountrySelector} from '../CountrySelector/CountrySelector'
import {COUNTRIES} from '../CountrySelector/CountrySelector.utils'
import {
  isFlagGatedCountry,
  isLiveCountry,
  isPartnerAccessCountry,
  useCountryRegistry,
} from '../../../utils/country-registry'
import {useAsync} from '../../../utils/use-async'
import {useAuthClient} from '../../../utils/use-auth-client'
import {TransactionType} from '../TransactionType'
import {Button} from '../../shared/Button'
import {
  StepCardWrapper,
  StepCard,
  FooterRow,
  FooterCenter,
} from '../AccountSetup/AccountSetup.styles'
import {useUserRoles} from '../../../utils/use-user-roles'
import {useUserFeatures} from '../../../context/user-features-context'
import {getTaxIdentifier} from './utils/taxIdentifier'
import {
  StyledAddIcon,
  StyledDeleteIcon,
  StyledLinkIcon,
} from '../../shared/icons/icons.styles'

// GEP-6957: only these countries have a fully supported, live account-creation
// path in prod today (pricing/billing end to end). Anything NOT in this list —
// including the PoC/Loqate-backed countries in CountrySelector.utils.js's
// COUNTRY_OPTIONS (ES/IT/IN/PH/VN/GR today) and any country added there in the
// future — is treated as unsupported by default, so a new country never
// silently becomes orderable without pricing/billing support behind it.
// Gated by the Enable_International_Account_Creation feature flag (features
// table, see user-features-context.js) rather than a hardcoded block, so it
// can be turned on later with a DB row insert instead of a code change/deploy.
// Unsupported countries are hidden from this dropdown (new account/location
// creation) while the flag is off, rather than reusing selectable-countries.js's
// HIDDEN_COUNTRY_CODES, which would also hide them from AccountEditModal/
// LocationForm/ProposalTool where existing accounts must stay editable
// regardless of this flag. Existing accounts already in an unsupported
// country are blocked at "Create Purchase Link" instead (see AllAccounts/
// MyAccounts handleOrder), showing the same "Almost there" message this
// dropdown used to show on selection.
// Which countries are fully live vs. behind Enable_International_Account_Creation
// now comes from the backend country registry (status 'enabled' vs 'flag_gated';
// see src/utils/country-registry.js). A country absent from the registry, or
// 'disabled', is always restricted — the flag is not a blanket bypass, since
// ooe-service's country validation rejects anything outside the registry too.
export const isRestrictedAccountCreationCountry = (
  code,
  enableInternationalAccountCreation = false,
) => {
  if (!code) return false
  if (isLiveCountry(code)) return false
  return !(enableInternationalAccountCreation && isFlagGatedCountry(code))
}

export const AccountInfo = forwardRef(
  (
    {
      keapPartnerId,
      externalSaving,
      onValidatingAddressChange,
      onCancel,
      showBackContinue,
      onBack,
      canGoBack,
      showContinue,
      onContinue,
      canContinue,
      isContinueLoading,
    },
    ref,
  ) => {
    const client = useAuthClient()
    const dispatch = useDispatch()
    const {run} = useAsync({status: 'pending'})
    const navigate = useNavigate()
    const {user} = useAuth0()

    const {roles} = useUserRoles()
    // PartnerOps shares the core Purchase Link state with Keap Partner and
    // Core OOE. The role remains relevant for country and legacy rollback
    // behavior below.
    const isPartnerOpsRole = roles.includes('PartnerOps')
    // New purchase links use the core location-aware wizard. This narrowly
    // scoped opt-in remains as an operational rollback for legacy KP11 links.
    const useLegacyKeapCatalog =
      process.env.REACT_APP_ENABLE_LEGACY_KP_CATALOG === 'true'
    const userFeatures = useUserFeatures()
    const {Enable_GB_Loc_Access, Enable_International_Account_Creation} =
      userFeatures

    const countryRegistry = useCountryRegistry()
    const filteredCountries = useMemo(() => {
      const skipToUS = roles.some(role =>
        ['VivialDemo', 'ThryvOnly'].includes(role),
      )
      const canUSAAUUser = roles.some(role =>
        ['PartnerOps', 'KeapPartner', 'InfluencerOps'].includes(role),
      )
      const canUseInternationalAccountCreation = roles.some(role =>
        ['PartnerOps', 'KeapPartner'].includes(role),
      )
      const canUSAUser = roles.some(role =>
        [
          'InsideDemo',
          'PremiseDemo',
          'Premise',
          'National',
          'reseller',
        ].includes(role),
      )
      const canAUUser = roles.some(role =>
        ['Inside', 'inside', 'Australia', 'australia'].includes(role),
      )
      const canNZUser = roles.some(role =>
        ['NZSales', 'nzsales', 'NewZealandSales', 'newzealandsales'].includes(
          role,
        ),
      )

      const list = skipToUS
        ? COUNTRIES.filter(c => c.code === 'US')
        : canUSAAUUser
        ? COUNTRIES.filter(c => isPartnerAccessCountry(c.code))
        : canUSAUser
        ? COUNTRIES.filter(c => ['US', 'CA'].includes(c.code))
        : canNZUser
        ? COUNTRIES.filter(c => c.code === 'NZ')
        : canAUUser
        ? COUNTRIES.filter(c => ['AU', 'NZ'].includes(c.code))
        : COUNTRIES

      return list.filter(c => {
        if (c.code === 'GB' && !Enable_GB_Loc_Access) return false
        if (
          (!Enable_International_Account_Creation ||
            !canUseInternationalAccountCreation) &&
          isRestrictedAccountCreationCountry(c.code)
        )
          return false
        return true
      })
    }, [
      roles,
      Enable_GB_Loc_Access,
      Enable_International_Account_Creation,
      countryRegistry,
    ])

    const {
      verifiedEmail,
      selectedTransactionType,
      selectedCountry,
      isMultiLocation,
      parents,
      activeAccordion,
      setActiveAccordion,
      isAddressValidating,
      addressSuggestion,
      resolveAddressSuggestion,
      addChild,
      discardChild,
      validateParentField,
      validateChildField,
      updateParent,
      updateChild,
      saveSingleLocParent,
      saveParentWithFirstChild,
      saveChild,
      handleTransactionTypeChange,
      handleCountryandRegionChange,
    } = useAccountTree(run, client)

    useImperativeHandle(ref, () => ({
      saveAndContinue: saveSingleLocParent,
    }))

    // AccountSetup shows its own "Creating account…" overlay for the whole
    // saveAndContinue() call, which otherwise overlaps this component's own
    // "Validating Address" overlay while the Google address check is still
    // running. Report the phase up so AccountSetup can hide its overlay
    // until address validation is actually done.
    useEffect(() => {
      onValidatingAddressChange?.(isAddressValidating)
    }, [isAddressValidating, onValidatingAddressChange])

    // Auto-default country whenever selectedCountry is cleared (mount + tab switch)
    useEffect(() => {
      if (selectedCountry) return
      const US_DEFAULT_ROLES = [
        'Premise',
        'PremisePilot',
        'PremiseDemo',
        'Admin',
        'DirectSales',
        'National',
        'InsideDemo',
        'Inbound LC',
        'LeadConverter',
        'KeapPartner',
        'PartnerOps',
        'InfluencerOps',
        'Reseller',
        'reseller',
      ]
      const AU_DEFAULT_ROLES = ['Inside', 'inside', 'Australia', 'australia']
      const NZ_DEFAULT_ROLES = [
        'NZSales',
        'nzsales',
        'NewZealandSales',
        'newzealandsales',
      ]
      if (roles.some(r => US_DEFAULT_ROLES.includes(r))) {
        handleCountryandRegionChange('US')
      } else if (roles.some(r => AU_DEFAULT_ROLES.includes(r))) {
        handleCountryandRegionChange('AU')
      } else if (roles.some(r => NZ_DEFAULT_ROLES.includes(r))) {
        handleCountryandRegionChange('NZ')
      }
    }, [selectedCountry, roles, handleCountryandRegionChange])

    // ─────────────────────────────────────────────
    // Derived helpers
    // ─────────────────────────────────────────────

    const isBootstrapParent = parent => {
      return (
        isMultiLocation &&
        !parent.saved &&
        parent.dirty &&
        parent.children?.length === 1 &&
        !parent.children[0]?.saved &&
        parent.children[0]?.dirty
      )
    }

    const canAddChild = parent => {
      return isMultiLocation && parent.saved
    }

    const showChildSave = child => {
      return child.dirty && !child.saved
    }

    const showChildOrder = child => {
      return child.saved
    }

    const showChildDiscard = (child, parent) => {
      return !child.saved && parent.saved
    }

    // Builds a PurchaseLinkSlice location entry for one saved child — used by
    // both handleOrder (single child, but seeding every saved sibling for the
    // switcher) and handleOrderForParent (only the parent-pay subset,
    // combined). child.response is minimal ({thryvId, ccId, ...} straight
    // from multi-location/location) — it carries no address/billTo/business
    // name, so every display field is sourced from child.values/parent.values,
    // same as the legacy branch this replaces.
    const buildLocationEntry = (child, parent, countryIso) => {
      const thryvId = child.response?.thryvId || child.response?.ccId
      const businessName =
        child.values?.businessName || parent.values?.businessName || ''
      const billTo = toLocationPayloadBillTo(
        child.values?.billTo || parent.values?.billTo,
      )
      return {
        locationId: thryvId,
        locationName: child.values?.locationName || businessName,
        thryvId,
        country: countryIso,
        billTo,
        email: child.values?.email || verifiedEmail,
        // This location's own identity, distinct from the parent-level
        // accountInfo set alongside it in handleOrder — buildSendLinkPayload
        // reads THIS for a single-location ('location' orderMode) order, so
        // an individual purchase link for one child in a multi-location
        // signup uses that child's own business name/contact/address instead
        // of the parent's, matching AllAccounts/MyAccounts' buildAccountInfo
        // (purchaseLinkState.js) and the same fix already applied to the
        // Keap-partner branch above (see its accountInformation comment).
        accountInfo: {
          thryvId,
          businessName,
          email: child.values?.email || verifiedEmail,
          firstName:
            child.values?.firstName || parent.values?.firstName || null,
          lastName: child.values?.lastName || parent.values?.lastName || null,
          phone: child.values?.telephone || parent.values?.telephone || null,
          mobile: child.values?.mobile || parent.values?.mobile || null,
          country: countryIso,
          xpCode: child.values?.xpCode || parent.values?.xpCode || null,
          salesRepOrPartner:
            child.values?.salesRepOrPartner ||
            parent.values?.salesRepOrPartner ||
            null,
          billTo,
          address: child.values?.address || parent.values?.address || null,
          city: child.values?.city || parent.values?.city || null,
          state:
            child.values?.state?.value ||
            child.values?.state ||
            parent.values?.state?.value ||
            parent.values?.state ||
            null,
          postCode: child.values?.postCode || parent.values?.postCode || null,
        },
      }
    }

    // A single (or no) parent-pay child has nothing to combine, so the
    // "Create Purchase Link" button must stay hidden — mirrors AccountRow's
    // hasParentPayChild (parentPayChildCount > 1) and handleOrderForParent's
    // length guard in AllAccounts/index.js and MyAccounts/index.js.
    const hasParentPaySavedChild = parent => {
      const countryIso = selectedCountry || 'US'
      const parentPaySavedChildCount = parent.children.filter(
        child =>
          child.saved &&
          buildLocationEntry(child, parent, countryIso).billTo !== 'self',
      ).length
      return parentPaySavedChildCount > 1
    }

    const handleOrder = async (child, parent) => {
      const countryIso = selectedCountry || 'US'
      // Keep the partner/client association for newly created locations even
      // though their order now proceeds through the core purchase-link flow.
      if (keapPartnerId && !useLegacyKeapCatalog) {
        try {
          if (user?.email && verifiedEmail) {
            await client('cc/order/keapClient/createClientPartnerMap', {
              data: {
                link_id: 0,
                keap_partner_id: keapPartnerId,
                keap_partner_email: user.email,
                keap_client_email: verifiedEmail,
                status: 'Created',
              },
            })
          }
        } catch (mapErr) {
          console.error('createClientPartnerMap (order-time) failed', mapErr)
        }
      }
      if (keapPartnerId && useLegacyKeapCatalog) {
        try {
          if (user?.email && verifiedEmail) {
            await client('cc/order/keapClient/createClientPartnerMap', {
              data: {
                link_id: 0,
                keap_partner_id: keapPartnerId,
                keap_partner_email: user.email,
                keap_client_email: verifiedEmail,
                status: 'Created',
              },
            })
          }
        } catch (mapErr) {
          console.error(
            'createClientPartnerMap (multi-loc order-time) failed',
            mapErr,
          )
        }
        navigate('/keap-partner-catalog', {
          state: {
            linkType: '1:1',
            isUpgrade: false,
            email: child.values?.email || verifiedEmail,
            accountCode: child.response?.thryvId || child.response?.ccId,
            selectedCountry: countryIso,
            keapPartnerId,
            childCustomerInfo: {
              company:
                child.values?.businessName || parent.values?.businessName || '',
              firstName: child.values?.firstName || '',
              lastName: child.values?.lastName || '',
              locationName: child.values?.locationName || '',
              accountCode: child.response?.thryvId || child.response?.ccId,
              billTo: child.values?.billTo || parent.values?.billTo || 'parent',
              xpCode: child.values?.xpCode || parent.values?.xpCode || '',
            },
            accountInformation: {
              email: child.values?.email || verifiedEmail,
              firstName: child.values?.firstName || '',
              lastName: child.values?.lastName || '',
              // This purchase link is FOR the child location, so the order's company
              // must be the CHILD's business name — every other field in this block is
              // sourced from the child. Previously this preferred the parent's
              // businessName, which sent the parent name as the order company and (via
              // cc-order-service updateRecurlyAccount) overwrote the child's own company
              // on the child Recurly account. Fall back to the parent only when the child
              // has no business name.
              company:
                child.values?.businessName || parent.values?.businessName || '',
              locationName: child.values?.locationName || '',
              address: child.values?.address || '',
              city: child.values?.city || '',
              state: child.values?.state || '',
              postCode: child.values?.postCode || '',
              country: child.values?.country || countryIso,
              phone: child.values?.phone || '',
              mobile: child.values?.mobile || '',
              billTo: child.values?.billTo || parent.values?.billTo || 'parent',
              // kp-11Link-generateUrl.js reads accountInformation.abn for AU/NZ orders;
              // without this the ABN/NZBN keyed in on the child (or parent) form never
              // reaches the purchase link, so it never reaches Recurly. NZ orders use
              // the separate 'nzbn' field key (see config/locationFields.js), not 'abn'.
              abn:
                getTaxIdentifier(child.values) ||
                getTaxIdentifier(parent.values),
            },
            thryvDetail: {
              thryvId: child.response?.thryvId || child.response?.ccId,
              // ccId must be the PARENT's code, not the child's. The location
              // creation response echoes the child's own code into its ccId field,
              // so sourcing it from child.response yields thryvId === ccId.
              ccId: parent.response?.ccId || child.response?.ccId,
              accountAlreadyCreated: true,
              locationName: child.values?.locationName || '',
              billTo: child.values?.billTo || parent.values?.billTo || 'parent',
            },
          },
        })
      } else if (isPartnerOpsRole && useLegacyKeapCatalog) {
        // PartnerOps: hand off to the legacy catalog.js → payment-details.js →
        // generatePartnerOpsLink pipeline, one link per location, same as
        // AllAccounts' handleSaveAndProceed (add-location) does for this role.
        const [all5PlanPrices, defaultPlanConfig] = await Promise.all([
          client('plans/5/v1/list').catch(() => null),
          client('plans/config/default').catch(() => null),
        ])
        const partnerOpsOrderState = buildNewLocationOrderState({
          response: {
            thryvId: child.response?.thryvId || child.response?.ccId,
            ccId: parent.response?.ccId || child.response?.ccId,
          },
          parentAccount: {billTo: parent.values?.billTo},
          editValues: child.values,
          country: countryIso,
          email: child.values?.email || verifiedEmail,
          all5PlanPrices,
          defaultPlanConfig,
        })
        // buildNewLocationOrderState only falls back to the child's own
        // abn/behalfRep values — this branch additionally falls back to the
        // parent's, since a PartnerOps child order can inherit either from
        // whichever form (parent or child) the rep actually filled in.
        partnerOpsOrderState.order.accountInformation.abn =
          getTaxIdentifier(child.values) || getTaxIdentifier(parent.values)
        Object.assign(
          partnerOpsOrderState.order.accountInformation,
          buildBehalfRepFieldsForChildOrder(child.values, parent.values),
        )
        navigate('/order/new', {
          state: partnerOpsOrderState,
        })
      } else {
        // Core OOE: seed PurchaseLinkSlice (same flow AllAccounts/MyAccounts and
        // handleSingleLocNext already use) instead of the legacy /order/new
        // hand-off this replaces, then advance the wizard in place — this
        // component is already rendered inside the AccountSetup page, so there's
        // no route change, mirroring handleSingleLocNext's dispatch(goNext()).
        // Only the clicked child is seeded — a single-location order is scoped
        // to that one location, with no switcher to browse its siblings.
        // Combining locations is what the header-level "Create Purchase Link"
        // (handleOrderForParent, orderMode: 'parentPay') is for; which button
        // the rep clicks decides single- vs multi-location, matching
        // AllAccounts' per-location button (which only shows a switcher when
        // its Edit-modal detour happens to carry sibling data along, which it
        // shouldn't rely on either).
        const all5PlanPrices = await (keapPartnerId
          ? fetchKeapPartnerCatalog(client, {country: countryIso, roles})
          : client('plans/5/v1/list')
        ).catch(() => null)
        const clickedLocationEntry = buildLocationEntry(
          child,
          parent,
          countryIso,
        )
        const locations = [clickedLocationEntry]
        const clickedLocationId = clickedLocationEntry.locationId

        dispatch(
          setProductConfigInfo(
            applyProductRulesBeforeLoading(all5PlanPrices, userFeatures),
          ),
        )
        dispatch(
          setAccountInfo({
            thryvId: parent.response?.ccId || clickedLocationId,
            businessName: parent.values?.businessName || '',
            email: verifiedEmail,
            firstName: parent.values?.firstName || null,
            lastName: parent.values?.lastName || null,
            phone: parent.values?.telephone || null,
            mobile: parent.values?.mobile || null,
            country: countryIso,
            xpCode: parent.values?.xpCode || null,
            salesRepOrPartner: parent.values?.salesRepOrPartner || null,
            billTo: toLocationPayloadBillTo(
              child.values?.billTo || parent.values?.billTo,
            ),
            // Forwarded to order_header via buildCreateOrderPayload so the
            // payment page's "Same as my business address" checkbox has an
            // actual address to copy from.
            address: parent.values?.address || null,
            city: parent.values?.city || null,
            state: parent.values?.state?.value || parent.values?.state || null,
            postCode: parent.values?.postCode || null,
          }),
        )
        dispatch(
          initLocations({
            locations,
            selectedLocationId: clickedLocationId,
            orderMode: 'location',
          }),
        )
        dispatch(goNext())
      }
    }

    // Header-level "Create Purchase Link" for a multi-location parent with
    // MORE THAN ONE saved, parent-pay child — combines every such child into
    // one purchase flow, mirroring AllAccounts' handleOrderForParent. A
    // single (or no) parent-pay child has nothing to combine.
    const handleOrderForParent = async parent => {
      const countryIso = selectedCountry || 'US'
      const parentPayChildren = parent.children.filter(
        child =>
          child.saved &&
          buildLocationEntry(child, parent, countryIso).billTo !== 'self',
      )
      if (parentPayChildren.length <= 1) return

      // KP11 links use a different catalog/cart representation from Core OOE.
      // Give KP and PartnerOps the same parent-pay semantics, but hand them to
      // the KP host with every child identity instead of the Core catalog.
      if ((keapPartnerId || isPartnerOpsRole) && useLegacyKeapCatalog) {
        const locations = parentPayChildren.map(child =>
          buildLocationEntry(child, parent, countryIso),
        )
        navigate('/keap-partner-catalog', {
          state: {
            linkType: '1:1',
            selectedCountry: countryIso,
            keapPartnerId: keapPartnerId || parent.values?.xpCode || null,
            partnerOps: isPartnerOpsRole,
            parentCcId: parent.response?.ccId,
            accountInformation: {
              businessName: parent.values?.businessName || '',
              email: verifiedEmail,
              country: countryIso,
            },
            multiLocationOrder: {
              orderMode: 'parentPay',
              selectedLocationId: locations[0]?.locationId,
              locations,
            },
          },
        })
        return
      }

      const all5PlanPrices = await (keapPartnerId
        ? fetchKeapPartnerCatalog(client, {country: countryIso, roles})
        : client('plans/5/v1/list')
      ).catch(() => null)
      const locations = parentPayChildren.map(child =>
        buildLocationEntry(child, parent, countryIso),
      )

      dispatch(
        setProductConfigInfo(
          applyProductRulesBeforeLoading(all5PlanPrices, userFeatures),
        ),
      )
      dispatch(
        setAccountInfo({
          thryvId: parent.response?.ccId || locations[0]?.thryvId,
          businessName: parent.values?.businessName || '',
          email: verifiedEmail,
          firstName: parent.values?.firstName || null,
          lastName: parent.values?.lastName || null,
          phone: parent.values?.telephone || null,
          mobile: parent.values?.mobile || null,
          country: countryIso,
          xpCode: parent.values?.xpCode || null,
          salesRepOrPartner: parent.values?.salesRepOrPartner || null,
          billTo: 'parent',
          // Forwarded to order_header via buildCreateOrderPayload so the
          // payment page's "Same as my business address" checkbox has an
          // actual address to copy from.
          address: parent.values?.address || null,
          city: parent.values?.city || null,
          state: parent.values?.state?.value || parent.values?.state || null,
          postCode: parent.values?.postCode || null,
        }),
      )
      dispatch(
        initLocations({
          locations,
          selectedLocationId: locations[0]?.locationId,
          orderMode: 'parentPay',
        }),
      )
      dispatch(goNext())
    }

    const isAnySaving = parents.some(
      p => p.saving || p.children?.some(c => c.saving),
    )

    const isLocked = parents.some(p => p.saved)

    return (
      <>
        {/* HEADER */}
        <PageHeader isAccountInfoComponent>
          <div>
            <PageTitle>Account Information</PageTitle>
            <PageSubtitle>
              Provide customer and business details to continue creating the
              order.
            </PageSubtitle>
          </div>

          {/* {isMultiLocation && (
            <MultiLocationBadge>Multi-location</MultiLocationBadge>
          )} */}
        </PageHeader>

        <StepCardWrapper isAccountInfoComponent>
          <StepCard>
            <PageWrapper>
              {/* SAVING OVERLAY — suppressed when parent (AccountSetup) already shows one */}
              {((isAnySaving && !externalSaving) || isAddressValidating) && (
                <LoadingOverlay>
                  <SpinnerRing />
                  {isAnySaving && !externalSaving && (
                    <SpinnerLabel>Saving…</SpinnerLabel>
                  )}
                  {isAddressValidating && (
                    <SpinnerLabel>Validating Address</SpinnerLabel>
                  )}
                </LoadingOverlay>
              )}

              {/* CONTROLS */}
              <TopControlsRow>
                <TransactionTypeColumn>
                  <TransactionType
                    onChange={handleTransactionTypeChange}
                    disabled={isLocked}
                  />
                </TransactionTypeColumn>

                <CountrySelectorColumn>
                  <CountrySelector
                    value={selectedCountry}
                    onChange={handleCountryandRegionChange}
                    disabled={isLocked}
                    countries={filteredCountries}
                  />
                </CountrySelectorColumn>
              </TopControlsRow>

              {/* ACCORDIONS */}
              {selectedTransactionType && (
                <AccordionWrapper>
                  {parents.map(parent => (
                    <AccordionParentWrap key={parent.id}>
                      {/* ───────────────────────────── PARENT ───────────────────────────── */}
                      <Accordion
                        isParent
                        isMultiLocation={isMultiLocation}
                        isOpen={activeAccordion === parent.id}
                        businessName={parent.values?.businessName}
                        onToggle={() =>
                          setActiveAccordion(
                            activeAccordion === parent.id ? null : parent.id,
                          )
                        }
                        badge={
                          isMultiLocation && (
                            <ParentBadge>Parent Account</ParentBadge>
                          )
                        }
                        actions={
                          <>
                            {canAddChild(parent) && (
                              <Button
                                variant="solid-dark-blue"
                                size="sm"
                                onClick={() => addChild(parent.id)}
                              >
                                <StyledAddIcon />
                                <span>Add Child</span>
                              </Button>
                            )}
                            {isMultiLocation &&
                              hasParentPaySavedChild(parent) && (
                                <Button
                                  variant="solid-dark-blue"
                                  size="sm"
                                  onClick={() => handleOrderForParent(parent)}
                                >
                                  <StyledLinkIcon />
                                  Create Purchase Link
                                </Button>
                              )}
                          </>
                        }
                      >
                        <AccountCard
                          type="parent"
                          account={parent}
                          errors={parent.errors}
                          touched={parent.touched}
                          disabled={!selectedCountry || parent.saved}
                          onChange={(field, value) =>
                            updateParent(parent, parent.id, field, value)
                          }
                          onBlur={field =>
                            validateParentField(
                              parent,
                              parent.id,
                              field,
                              parent.values[field],
                            )
                          }
                        />
                      </Accordion>

                      {/* ───────────────────────────── CHILDREN ───────────────────────────── */}
                      {isMultiLocation &&
                        parent.children.map(child => (
                          <AccordionChildWrap key={child.id}>
                            <Accordion
                              isMultiLocation={isMultiLocation}
                              isOpen={activeAccordion === child.id}
                              businessName={child.values?.businessName}
                              onToggle={() =>
                                setActiveAccordion(
                                  activeAccordion === child.id
                                    ? null
                                    : child.id,
                                )
                              }
                              badge={<ChildBadge>Child Account</ChildBadge>}
                              actions={
                                <>
                                  {showChildOrder(child) && (
                                    <Button
                                      variant="solid-dark-blue"
                                      size="sm"
                                      onClick={e => {
                                        e.stopPropagation()
                                        handleOrder(child, parent)
                                      }}
                                    >
                                      <StyledLinkIcon />
                                      Create Purchase Link
                                    </Button>
                                  )}
                                  {showChildDiscard(child, parent) && (
                                    <Button
                                      variant="secondary-danger"
                                      size="sm"
                                      onClick={e => {
                                        e.stopPropagation()
                                        discardChild(child.id, parent.id)
                                      }}
                                    >
                                      <StyledDeleteIcon />
                                      Delete
                                    </Button>
                                  )}
                                </>
                              }
                            >
                              <AccountCard
                                type="child"
                                account={child}
                                errors={child.errors}
                                touched={child.touched}
                                disabled={!selectedCountry || child.saved}
                                onChange={(field, value) =>
                                  updateChild(
                                    child,
                                    parent.id,
                                    child.id,
                                    field,
                                    value,
                                  )
                                }
                                onBlur={field =>
                                  validateChildField(
                                    child,
                                    parent.id,
                                    child.id,
                                    field,
                                    child.values[field],
                                  )
                                }
                              />

                              {/* ───────────────────────────── SAVE BUTTONS AREA ───────────────────────────── */}

                              {showChildSave(child) &&
                                !isBootstrapParent(parent) && (
                                  <DraftBanner>
                                    <DraftActions>
                                      <Button
                                        variant="solid-gradiant"
                                        size="sm"
                                        disabled={child.saving}
                                        onClick={async () => {
                                          const result = await saveChild(
                                            parent.id,
                                            child.id,
                                          )
                                          if (result?.success) {
                                            // Attribute account to partner at save time so
                                            // it appears in My Accounts even if Close is
                                            // clicked before Create Purchase Link.
                                            if (
                                              keapPartnerId &&
                                              user?.email &&
                                              verifiedEmail
                                            ) {
                                              try {
                                                await client(
                                                  'cc/order/keapClient/createClientPartnerMap',
                                                  {
                                                    data: {
                                                      link_id: 0,
                                                      keap_partner_id:
                                                        keapPartnerId,
                                                      keap_partner_email:
                                                        user.email,
                                                      keap_client_email:
                                                        verifiedEmail,
                                                      status: 'Created',
                                                    },
                                                  },
                                                )
                                              } catch (mapErr) {
                                                console.error(
                                                  'createClientPartnerMap (multi-loc save-time) failed',
                                                  mapErr,
                                                )
                                              }
                                            }
                                          }
                                          if (result && !result.success) {
                                            requestAnimationFrame(() => {
                                              const firstInvalid =
                                                document.querySelector(
                                                  '[aria-invalid="true"]',
                                                )
                                              if (firstInvalid) {
                                                firstInvalid.scrollIntoView({
                                                  behavior: 'smooth',
                                                  block: 'center',
                                                })
                                                firstInvalid.focus({
                                                  preventScroll: true,
                                                })
                                              }
                                            })
                                          }
                                        }}
                                      >
                                        Save
                                      </Button>
                                    </DraftActions>
                                  </DraftBanner>
                                )}
                            </Accordion>
                          </AccordionChildWrap>
                        ))}
                      {isBootstrapParent(parent) && (
                        <DraftBanner>
                          <DraftActions>
                            <Button
                              variant="solid-gradiant"
                              size="sm"
                              disabled={parent.saving}
                              onClick={async () => {
                                const result = await saveParentWithFirstChild(
                                  parent,
                                )
                                if (result?.success) {
                                  // Attribute account to partner at save time so
                                  // it appears in My Accounts even if Close is
                                  // clicked before Create Purchase Link.
                                  if (
                                    keapPartnerId &&
                                    user?.email &&
                                    verifiedEmail
                                  ) {
                                    try {
                                      await client(
                                        'cc/order/keapClient/createClientPartnerMap',
                                        {
                                          data: {
                                            link_id: 0,
                                            keap_partner_id: keapPartnerId,
                                            keap_partner_email: user.email,
                                            keap_client_email: verifiedEmail,
                                            status: 'Created',
                                          },
                                        },
                                      )
                                    } catch (mapErr) {
                                      console.error(
                                        'createClientPartnerMap (multi-loc save-time) failed',
                                        mapErr,
                                      )
                                    }
                                  }
                                }
                                if (result && !result.success) {
                                  // Open the accordion that contains errors so its
                                  // fields are in the DOM before we try to scroll.
                                  // Parent errors take priority; fall back to child.
                                  const hasParentErrors =
                                    result.parentErrors &&
                                    Object.keys(result.parentErrors).length > 0
                                  const hasChildErrors =
                                    result.childErrors &&
                                    Object.keys(result.childErrors).length > 0
                                  if (hasParentErrors) {
                                    setActiveAccordion(parent.id)
                                  } else if (hasChildErrors) {
                                    const firstChild = parent.children?.[0]
                                    if (firstChild)
                                      setActiveAccordion(firstChild.id)
                                  }
                                  // Double rAF: first tick lets React commit the
                                  // accordion open; second tick lets the browser
                                  // paint so the fields are queryable.
                                  requestAnimationFrame(() => {
                                    requestAnimationFrame(() => {
                                      const firstInvalid =
                                        document.querySelector(
                                          '[aria-invalid="true"]',
                                        )
                                      if (firstInvalid) {
                                        firstInvalid.scrollIntoView({
                                          behavior: 'smooth',
                                          block: 'center',
                                        })
                                        firstInvalid.focus({
                                          preventScroll: true,
                                        })
                                      }
                                    })
                                  })
                                }
                              }}
                            >
                              Save
                            </Button>
                          </DraftActions>
                        </DraftBanner>
                      )}
                    </AccordionParentWrap>
                  ))}
                </AccordionWrapper>
              )}

              <AddressSuggestionModal
                suggestion={addressSuggestion}
                onConfirm={resolveAddressSuggestion}
              />
            </PageWrapper>
          </StepCard>

          <AccountInfoFooter
            onCancel={onCancel}
            showBackContinue={showBackContinue}
            onBack={onBack}
            canGoBack={canGoBack}
            showContinue={showContinue}
            onContinue={onContinue}
            canContinue={canContinue}
            isContinueLoading={isContinueLoading}
          />
        </StepCardWrapper>
      </>
    )
  },
)

AccountInfo.displayName = 'AccountInfo'

// This step's own footer card — a sibling below StepCard (see
// AccountSetup/index.js), not inside it. Kept as a separate component from
// AccountInfo itself (rather than folded into its forwardRef body) since the
// Continue action here is driven by AccountSetup/index.js's
// handleSingleLocNext (which calls AccountInfo's saveAndContinue via ref),
// not by any state internal to AccountInfo.
export function AccountInfoFooter({
  onCancel,
  showBackContinue,
  onBack,
  canGoBack,
  showContinue,
  onContinue,
  canContinue,
  isContinueLoading,
}) {
  return (
    <FooterRow>
      <FooterCenter>
        <Button variant="secondary-gray" size="sm" onClick={onCancel}>
          Close
        </Button>
      </FooterCenter>

      {showBackContinue && (
        <FooterCenter>
          <Button
            variant="secondary-blue"
            size="sm"
            onClick={onBack}
            disabled={!canGoBack}
          >
            &lt; Back
          </Button>

          <Button
            variant="secondary-blue"
            size="sm"
            onClick={onContinue}
            disabled={!canContinue}
          >
            Continue &gt;
          </Button>
        </FooterCenter>
      )}

      {showContinue && (
        <FooterCenter>
          <Button
            variant="solid-gradiant"
            size="sm"
            onClick={onContinue}
            disabled={!canContinue}
            isLoading={isContinueLoading}
          >
            Continue &gt;
          </Button>
        </FooterCenter>
      )}
    </FooterRow>
  )
}

// import React, {forwardRef, useImperativeHandle} from 'react'
// import {useAccountTree} from './hooks/useAccountTree'
// import {AccountCard} from './components/AccountCard'
// import {
//   AccordionWrapper,
//   ChildBadge,
//   CountrySelectorColumn,
//   // DiscardButton,
//   DraftActions,
//   DraftBanner,
//   MultiLocationBadge,
//   PageHeader,
//   PageSubtitle,
//   PageTitle,
//   ParentBadge,
//   SaveButton,
//   TopControlsRow,
//   TransactionTypeColumn,
// } from '../AccountInfo/AccountInfo.style'
// import {Accordion} from './components/Accordion'
// import {CountrySelector} from '../CountrySelector/CountrySelector'
// // import {
// //   selectAccountInfoState,
// //   selectCountrySelectState,
// //   selectEmailLookupState,
// //   selectTransactionTypeState,
// // } from '../../../app-store/AccountSetupSlice'
// // import {useDispatch, useSelector} from 'react-redux'
// import {useAsync} from '../../../utils/use-async'
// import {useAuthClient} from '../../../utils/use-auth-client'
// import {TransactionType} from '../TransactionType'
// // import {useLocationSave} from './hooks/useLocationSave'
// import {Button} from '../../shared/Button'

// export const AccountInfo = forwardRef((props, ref) => {
//   const client = useAuthClient()

//   const {run} = useAsync({
//     status: 'pending',
//   })
//   const {
//     verifiedEmail,

//     selectedTransactionType,

//     selectedCountry,

//     isMultiLocation,

//     parents,

//     activeAccordion,

//     setActiveAccordion,

//     addChild,

//     updateParent,

//     updateChild,

//     saveSingleLocParent,

//     handleTransactionTypeChange,

//     handleCountryandRegionChange,
//   } = useAccountTree(run, client)

//   useImperativeHandle(ref, () => ({
//     saveAndContinue: saveSingleLocParent,
//   }))

//   // const {saveSingleLocParent, saveStatus, saveError, isSaving} =
//   //   useLocationSave({
//   //     run,
//   //     client,
//   //     setParents,
//   //     isMultiLocation,
//   //   })

//   return (
//     <div>
//       {/* Header */}

//       <PageHeader>
//         <div>
//           <PageTitle>Account Information</PageTitle>

//           <PageSubtitle>
//             Provide customer and business details to continue creating the
//             order.
//           </PageSubtitle>
//         </div>

//         {isMultiLocation && (
//           <MultiLocationBadge>Multi-location</MultiLocationBadge>
//         )}
//       </PageHeader>

//       <TopControlsRow>
//         <TransactionTypeColumn>
//           <TransactionType
//             onChange={transactionType =>
//               handleTransactionTypeChange(transactionType)
//             }
//           />
//         </TransactionTypeColumn>

//         <CountrySelectorColumn>
//           <CountrySelector
//             value={selectedCountry}
//             onChange={country => handleCountryandRegionChange(country)}
//             // disable={parent.saved}
//           />
//         </CountrySelectorColumn>
//       </TopControlsRow>

//       {/* Forms */}

//       {selectedTransactionType && (
//         <AccordionWrapper>
//           {parents.map(parent => (
//             <div key={parent.id}>
//               <Accordion
//                 isParent={true}
//                 isMultiLocation={isMultiLocation}
//                 isOpen={activeAccordion === parent.id}
//                 onToggle={() => {
//                   // // if (isMultiLocation) {
//                   setActiveAccordion(
//                     activeAccordion === parent.id ? null : parent.id,
//                   )
//                   // }
//                 }}
//                 badge={
//                   isMultiLocation && <ParentBadge>Parent Account</ParentBadge>
//                 }
//                 actions={
//                   isMultiLocation &&
//                   parent.saved && (
//                     <SaveButton
//                       type="button"
//                       onClick={() => addChild(parent.id)}
//                     >
//                       + Add Child
//                     </SaveButton>
//                   )
//                 }
//               >
//                 <AccountCard
//                   type="parent"
//                   account={parent}
//                   errors={parent.errors}
//                   touched={parent.touched}
//                   disabled={!selectedCountry || parent.saved}
//                   onChange={(field, value) =>
//                     updateParent(parent.id, field, value)
//                   }
//                 />
//               </Accordion>
//               {console.log('parent.children', parent.children)}
//               {isMultiLocation &&
//                 parent.children.map(child => (
//                   <div key={child.id}>
//                     <Accordion
//                       isMultiLocation={isMultiLocation}
//                       isOpen={activeAccordion === child.id}
//                       onToggle={() =>
//                         setActiveAccordion(
//                           activeAccordion === child.id ? null : child.id,
//                         )
//                       }
//                       badge={<ChildBadge>Children Account</ChildBadge>}
//                       actions={
//                         parent.dirty &&
//                         child.dirty &&
//                         !parent.saved && (
//                           <DraftBanner>
//                             <DraftActions>
//                               <Button
//                                 variant="solid"
//                                 disabled={child.saved}
//                                 onClick={() =>
//                                   saveParentWithFirstChild(parent)
//                                 }
//                               >
//                                 {'Save'}
//                               </Button>
//                               {child.saved && (
//                                 <Button
//                                   variant="solid"
//                                 >
//                                   {'Order'}
//                                 </Button>
//                               )}
//                             </DraftActions>
//                           </DraftBanner>
//                         )
//                       }
//                     >
//                       <AccountCard
//                         type="child"
//                         account={child}
//                         errors={child.errors}
//                         touched={child.touched}
//                         disabled={!selectedCountry || parent.saved}
//                         onChange={(field, value) =>
//                           updateChild(parent.id, child.id, field, value)
//                         }
//                       />
//                     </Accordion>
//                   </div>
//                 ))}
//             </div>
//           ))}
//         </AccordionWrapper>
//       )}
//     </div>
//   )
// })
